import React, { useMemo } from "react";
import { Tabs } from "antd";
import { useLocation, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { getResource } from "../../util/getResourcePermission";
import { getRoles } from "../../util/getRoles";
import { isHiddenForSubAdmin } from "../../util/subAdminAccess";
import IntlMessages from "../../util/IntlMessages";
import { matchesSubmenuPath } from "../../util/matchesSubmenuPath";
import config from "../../permission/config.json";
import { useRoleMenu } from "../../hooks/useRoleMenu";

const { TabPane } = Tabs;

// This component renders tabs below the header for submenu items
// It uses the same permission logic as SidebarContent
const SubmenuTabs = () => {
  const location = useLocation();
  const history = useHistory();
  // Mirrors ALWAYS_ALLOWED_PATH_SUFFIXES in SidebarContent.js. "/frs-management"
  // (Face Identity Vault) has no RBAC resource and no plan module, and is meant to
  // be always visible wherever it is listed - currently the HR "More" menu.
  const ALWAYS_ALLOWED_PATH_SUFFIXES = ["/dashboard", "/my-profile", "/frs-management"];

  // Get menu array from hook - it will build menu arrays the same way SidebarContent does
  const menuArray = useRoleMenu();

  // Find which submenu the current route belongs to
  // Priority: Check more specific submenus first (Audit, Management, etc.) before generic ones (Me)
  // Exception: For /audits path, check "Me" submenu first if it exists there (for HR role)
  const findCurrentSubmenu = (menuArray) => {
    if (!menuArray || !Array.isArray(menuArray)) return null;

    // Special handling for /audits path - check "Me" submenu first if it exists
    // This handles HR role where /audits exists in both "Me" and "Audit" submenus
    // if (location.pathname.includes('/audits') && !location.pathname.includes('/audit-history')) {
    //   const meSubmenu = menuArray.find(item => 
    //     item.submenu && 
    //     (item.key === "Me" || item.key === "sub15") &&
    //     item.subMenuInfo && 
    //     Array.isArray(item.subMenuInfo) &&
    //     item.subMenuInfo.some(subItem => 
    //       subItem.path && matchesSubmenuPath(location.pathname, subItem.path)
    //     )
    //   );
    //   if (meSubmenu) {
    //     return meSubmenu;
    //   }
    // }

    // Priority order: Check specific submenus first, then generic ones
    // This ensures that when a path exists in multiple submenus (e.g., /audit-history in both "Me" and "Audit"),
    // we prioritize the more specific submenu
    const priorityOrder = ["sub20", "sub17", "sub19", "sub22", "sub18", "sub16", "sub15", "Me"];

    // First, collect all submenus with their priority
    const submenus = menuArray
      .filter(item => item.submenu && item.subMenuInfo && Array.isArray(item.subMenuInfo))
      .map(item => ({
        item,
        priority: priorityOrder.indexOf(item.key) !== -1
          ? priorityOrder.indexOf(item.key)
          : priorityOrder.length // Lower priority for unknown submenus
      }))
      .sort((a, b) => a.priority - b.priority); // Sort by priority (lower number = higher priority)

    // Check submenus in priority order
    // But for paths that exist in multiple submenus, use exact path matching first
    // This handles cases like /audits vs /audit-history
    for (const { item } of submenus) {
      const matchingSubItem = item.subMenuInfo.find((subItem) => {
        if (!subItem.path) return false;
        // Use exact path matching - the updated matchesSubmenuPath handles /audits vs /audit-history distinction
        return matchesSubmenuPath(location.pathname, subItem.path);
      });

      if (matchingSubItem) {
        return item;
      }
    }

    return null;
  };

  const isAlwaysAllowedPath = (path = "") =>
    ALWAYS_ALLOWED_PATH_SUFFIXES.some((suffix) => String(path || "").endsWith(suffix));

  const pathToSidebarModuleSlug = (path = "") => {
    const p = String(path || "").toLowerCase();
    if (p.endsWith("/dashboard")) return "dashboard";
    if (p.endsWith("/my-in-out")) return "my_in_out";
    if (p.endsWith("/leave-application")) return "leave_application";
    if (p.endsWith("/employee-attendence-regularization")) return "attendance_regularization";
    if (p.endsWith("/resignation")) return "resignation";
    if (p.endsWith("/tickets")) return "tickets";
    if (p.endsWith("/user-assets")) return "user_assets";
    if (p.endsWith("/user-reports")) return "user_reports";
    if (p.endsWith("/individual-rank")) return "individual_rank";
    if (p.endsWith("/bill-reimbursement")) return "bill_reimbursement";
    if (p.endsWith("/employee-master")) return "employee_master";
    if (p.endsWith("/employee-in-out")) return "employee_in_out";
    if (p.endsWith("/resignation-employee-list")) return "resignation_employee_list";
    if (p.endsWith("/last-working-day-emps")) return "last_working_day_list";
    if (p.endsWith("/admin-reports")) return "admin_reports";
    if (p.endsWith("/employee-tickets")) return "employee_tickets";
    if (p.endsWith("/admin-leave-approval")) return "admin_leave_management";
    if (p.endsWith("/user-leave-approve")) return "user_leave_management";
    if (p.endsWith("/pending-task-list")) return "pending_task";
    if (p.endsWith("/admin-regularizatrion")) return "admin_regularization";
    if (p.endsWith("/manage-team-regularization")) return "manage_team_regularization";
    if (p.endsWith("/company-doc-master")) return "doc_master";
    if (p.endsWith("/onboarding-master")) return "onboarding_master";
    if (p.endsWith("/candidate-approval-master")) return "candidate_approval_master";
    if (p.endsWith("/onboarding-initiation-master")) return "onboarding_initiation_master";
    if (p.endsWith("/employee-master-form-builder")) return "employee_master_form_builder";
    if (p.endsWith("/rejoin-employee-form-builder")) return "rejoin_employee_form_builder";
    if (p.endsWith("/candidate-approval")) return "candidate_approval";
    if (p.endsWith("/candidate-list")) return "candidate_list";
    if (p.endsWith("/onboarding-initiation")) return "onboarding_initiation";
    if (p.endsWith("/doc-approve-list")) return "doc_approve_list";
    if (p.endsWith("/new-joinees-list")) return "new_joinees_list";
    if (p.endsWith("/mood-list")) return "mood_analytics";
    if (p.includes("/jobs/")) return "job_opening";
    if (p.endsWith("/team-goals")) return "team_goals";
    if (p.endsWith("/inventory")) return "inventory";
    if (p.endsWith("/viewemployeedetails")) return "employee_details";
    if (p.endsWith("/organization-policy")) return "organization_policy";
    if (p.includes("/admin-bill-reimbursement")) return "admin_bill_reimbursement";
    if (p.includes("/engage/announcements")) return "announcement";
    if (p.includes("/engage/celebrations")) return "celebration";
    if (
      p.includes("/engage/wall") ||
      p.includes("/engage/awards") ||
      p.includes("/engage/badges") ||
      p.includes("/engage/moderation") ||
      p.includes("/engage/settings")
    ) return "wall";
    if (p.endsWith("/announcement") || p.endsWith("/announcement-category")) return "announcement";
    if (p.endsWith("/360-survey") || p.includes("/survey-form")) return "360_survey";
    if (p.endsWith("/360-questionnaire") || p.includes("/add/new-questionnaire")) return "questionnaire";
    if (p.endsWith("/360-reviews") || p.includes("/add/new-review") || p.includes("/360_reports")) return "360_reviews";
    if (p.endsWith("/activity-logs")) return "activity_logs";
    if (p.endsWith("/organization-view")) return "organization_tree";
    if (p.endsWith("/mira")) return "mira_ai";
    return "";
  };

  // Mirrors moduleAliases in hasEnabledModuleSlug (SidebarContent.js): one sidebar
  // entry can be served by more than one plan-catalog slug.
  const PLAN_MODULE_ALIASES = {
    admin_regularization: ["admin_regularization", "attendance_regularization"],
    manage_team_regularization: ["manage_team_regularization", "attendance_regularization"],
    user_leave_management: ["user_leave_management", "leave_application"],
    goals: ["goals", "my_goals", "team_goals"],
  };

  // Two-step check, identical in order and semantics to hasSidebarItemAccess in
  // SidebarContent.js - the tabs must show exactly the submenu entries the sidebar
  // shows, so both gates have to be applied here too.
  const hasHeaderItemAccess = (subItem) => {
    const itemPath = subItem?.path || "";
    if (isAlwaysAllowedPath(itemPath)) return true;

    let sidebarModuleAccess = new Set();
    try {
      const raw = JSON.parse(localStorage.getItem("sidebarModuleAccess") || "[]");
      sidebarModuleAccess = new Set(Array.isArray(raw) ? raw : []);
    } catch (error) {
      sidebarModuleAccess = new Set();
    }
    const hasPlanConfig = sidebarModuleAccess.size > 0;
    const slug = pathToSidebarModuleSlug(itemPath);

    // Overview is the Wall/Awards/Badges dashboard, so it belongs to the Wall
    // & Recognition cluster only - accessible when the "wall" plan module is
    // granted AND the per-role Engage Panel resource is granted (see
    // engage-wall-rbac-split memory - plan alone must not bypass RBAC here).
    // Enabling only Announcements or Celebrations must not surface it.
    if (hasPlanConfig && itemPath.includes("/engage/overview")) {
      const planGranted = sidebarModuleAccess.has("wall");
      return planGranted && (subItem?.permission || []).some((permission) => getResource(permission, ["read"]));
    }

    // Engage Wall + its admin-panel siblings (Awards/Badges/Moderation/
    // Settings) all share the "wall" plan slug but must also respect the
    // per-role RBAC resource (Engage Wall / Engage Panel) - every other
    // slug-mapped tab keeps the original plan-only behavior.
    if (hasPlanConfig && slug === "wall") {
      return sidebarModuleAccess.has(slug) && (subItem?.permission || []).some((permission) => getResource(permission, ["read"]));
    }

    // Celebrations is NOT plan-gated or permission-gated - no RBAC resource,
    // always shows, by design (2026-09-15).
    if (slug === "celebration") return true;

   // Keep tabs strictly aligned with sidebar visibility when plan modules are configured.
    if (hasPlanConfig && slug) return sidebarModuleAccess.has(slug);

    // Step 2: Internal RBAC check. Previously skipped whenever a plan was
    // configured, which let tabs appear for modules the sidebar hid.
    if (!subItem?.permission || subItem.permission.length === 0) return true;
    return subItem.permission.some((permission) => getResource(permission, ["read"]));
  };

  // Get filtered submenu items based on permissions (same logic as SidebarContent)
  // IMPORTANT: Only show items that user actually has permission for (matches sidebar exactly)
  const getFilteredSubmenuItems = (submenu) => {
    if (!submenu || !submenu.subMenuInfo) return [];

    const isAdmin = getRoles(["Admin"]);
    const isHR = getRoles(["HR"]);
    // Sub-Admin shares Admin's menu but holds only the permissions granted to
    // it: a tab needs a granted resource, or must be one of the personal pages
    // routes/newRoutes.js lets Sub-Admin open without one.
    const isSubAdmin = getRoles(["Sub-Admin"]);
    const SUB_ADMIN_OPEN_PATH_SUFFIXES = ["/my-profile", "/dashboard", "/team-leave-calender", "/engage/announcements", "/engage/celebrations", "/announcement", "/notification-settings", "/book-meeting-room"];
    const subAdminCanOpen = (subItem) =>
      subItem?.permission?.length > 0
        ? subItem.permission.some((permission) => getResource(permission, ["read"]))
        : SUB_ADMIN_OPEN_PATH_SUFFIXES.some((suffix) => String(subItem?.path || "").endsWith(suffix));

    return submenu.subMenuInfo.filter((subItem) => {
      // Check Admin or HR restriction
      if (subItem.isAdminOrHR && !(isAdmin || isHR || isSubAdmin)) {
        return false;
      }
      if (isSubAdmin && !subAdminCanOpen(subItem)) return false;
      // Settings/master screens are never shown to Sub-Admin.
      if (isHiddenForSubAdmin(subItem?.path)) return false;

      return hasHeaderItemAccess(subItem);
    });
  };

  const currentSubmenu = useMemo(() => findCurrentSubmenu(menuArray), [location.pathname, menuArray]);
  const filteredItems = useMemo(() => {
    if (!currentSubmenu) return [];
    return getFilteredSubmenuItems(currentSubmenu);
  }, [currentSubmenu]);

  // Don't render if no submenu found or no items
  if (!currentSubmenu || filteredItems.length === 0) {
    return null;
  }

  // Find active tab key
  const activeKey = filteredItems.find((item) =>
    matchesSubmenuPath(location.pathname, item.path)
  )?.key || filteredItems[0]?.key;

  const handleTabChange = (key) => {
    const selectedItem = filteredItems.find((item) => item.key === key);
    if (selectedItem && selectedItem.path) {
      history.push(selectedItem.path);
    }
  };

  return (
    <div
      className="header-submenu-tabs"
      style={{
        background: '#fff',
        padding: '0 24px',
      }}>
      <Tabs
        activeKey={activeKey}
        onChange={handleTabChange}
        type="line"
        style={{ marginBottom: 0 }}
      >
        {filteredItems.map((item) => (
          <TabPane
            key={item.key}
            tab={<IntlMessages id={item.title} />}
          />
        ))}
      </Tabs>
    </div>
  );
};

export default SubmenuTabs;
